import { useState } from "react";
import NumberDraft from "./NumberDraft";
export default function CalibrationEditor({
  value,
  onChange,
  onSave,
  onReset,
  onDiscard,
  busy,
  dirty,
  revision,
  disabled,
}) {
  const [eye, setEye] = useState("A");
  const identities = Object.keys(value.cameras).sort();
  const identity = identities[eye === "A" ? 0 : 1] || identities[0];
  const camera = value.cameras[identity];
  const cameraChange = (key, v) =>
    onChange({
      ...value,
      cameras: { ...value.cameras, [identity]: { ...camera, [key]: v } },
    });
  const fields = [
    ["center_x", "Centre X", 0, 1, 0.001],
    ["center_y", "Centre Y", 0, 1, 0.001],
    ["radius", "Lens radius", 0.05, 1, 0.001],
    ["fov_deg", "Lens field of view", 90, 240, 0.1],
    ["roll_deg", "Roll", -180, 180, 0.1],
    ["yaw_deg", "Optical-axis yaw", -360, 360, 0.1],
  ];
  const number = (key, label, min, max, step, val, change) => (
    <div className="field-control-row" key={key}>
      <label htmlFor={`cal-${key}`}>
        {label}
        <NumberDraft
          aria-label={`${label} value`}
          min={min}
          max={max}
          step={step}
          value={val}
          disabled={disabled || busy}
          onChange={change}
        />
      </label>
      <input
        id={`cal-${key}`}
        aria-label={label}
        type="range"
        min={min}
        max={max}
        step={step}
        value={val}
        disabled={disabled || busy}
        onChange={(e) => change(Number(e.target.value))}
      />
    </div>
  );
  return (
    <section className="field-panel">
      <div className="field-panel-head">
        <div>
          <h2>Lens calibration</h2>
          <p>
            {revision
              ? `Saved revision ${revision}`
              : "Device-specific alignment"}{" "}
            · {dirty ? "Unsaved preview" : "Saved settings"}
          </p>
        </div>
      </div>
      <div className="field-calibration">
        <div className="field-segment" style={{ marginBottom: 20 }}>
          {["A", "B"].slice(0, identities.length).map((e) => (
            <button
              key={e}
              className={eye === e ? "active" : ""}
              aria-pressed={eye === e}
              onClick={() => setEye(e)}
            >
              Camera {e}
            </button>
          ))}
        </div>
        <p className="field-caption" style={{ marginBottom: 16 }}>
          {identity}
        </p>
        {fields.map(([k, l, min, max, step]) =>
          number(k, l, min, max, step, camera[k], (v) => cameraChange(k, v)),
        )}
        <div className="field-checks">
          {["flip_x", "flip_y"].map((k) => (
            <label key={k}>
              <input
                type="checkbox"
                checked={camera[k]}
                disabled={disabled || busy}
                onChange={(e) => cameraChange(k, e.target.checked)}
              />
              {k === "flip_x" ? "Flip X" : "Flip Y"}
            </label>
          ))}
        </div>
        <hr style={{ borderColor: "#e8eff1", margin: "18px 0" }} />
        {number(
          "heading_deg",
          "Opening direction",
          -180,
          180,
          1,
          value.heading_deg,
          (v) => onChange({ ...value, heading_deg: v }),
        )}
        {number(
          "seam_deg",
          "Seam feathering",
          0,
          30,
          0.1,
          value.seam_deg,
          (v) => onChange({ ...value, seam_deg: v }),
        )}
        <div className="field-actions">
          <button
            className="field-button primary"
            disabled={!dirty || busy || disabled}
            onClick={onSave}
          >
            {busy ? "Saving…" : "Save calibration"}
          </button>
          <button
            className="field-button"
            disabled={busy || disabled}
            onClick={onReset}
          >
            Reset preview
          </button>
        </div>
        <button
          className="field-button"
          disabled={!dirty || busy || disabled}
          onClick={onDiscard}
        >
          Discard preview
        </button>
        <p className="field-caption">
          Centre uses frame width/height; radius uses the shorter frame
          dimension. Optical yaw aligns each lens. Opening direction rotates the
          viewer. Changes preview locally until saved for this node; original
          frames stay untouched. Hatched areas have no camera coverage.
        </p>
      </div>
    </section>
  );
}
